import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type {
  ClientContactChannel,
  ClientContactDirection,
  WorkItemDetail,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { CONTACT_CHANNELS, CONTACT_DIRECTIONS } from "./workItemVocabulary";

/**
 * Writing down the call a piece of client service was.
 *
 * <p>Saved in whatever state it is in, half-finished included. Completing the item is what
 * demands all of it, and refusing to save a partial note would send people to keep it somewhere
 * else until it was finished — which is where notes go to be lost.
 *
 * <p>What is still outstanding is said plainly at the bottom rather than by disabling Save, so
 * the person can see what Complete will ask for without having to try it.
 */
export function ClientServiceDrawer({
  item,
  open,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [direction, setDirection] = useState<ClientContactDirection | "">("");
  const [contactName, setContactName] = useState("");
  const [clientName, setClientName] = useState("");
  const [channel, setChannel] = useState<ClientContactChannel | "">("");
  const [whatWasExplained, setWhatWasExplained] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const held = item.clientService;
    setDirection(held?.direction ?? "");
    setContactName(held?.contactName ?? "");
    setClientName(held?.clientName ?? "");
    setChannel(held?.channel ?? "");
    setWhatWasExplained(held?.whatWasExplained ?? "");
    setError(null);
  }, [open, item]);

  /* Worked out here as well as on the server, so the sentence appears as somebody types rather
     than only once they have saved and tried to close the item. */
  const missing = [
    direction === "" ? "who called whom" : null,
    contactName.trim() === "" ? "the name of the person you spoke to" : null,
    clientName.trim() === "" ? "which client they are with" : null,
    channel === "" ? "how you reached them" : null,
    whatWasExplained.trim() === "" ? "what you explained" : null,
  ].filter((owed): owed is string => owed != null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/v1/work-items/${item.id}/client-service`, {
        direction: direction === "" ? null : direction,
        contactName: contactName.trim() || null,
        clientName: clientName.trim() || null,
        channel: channel === "" ? null : channel,
        whatWasExplained: whatWasExplained.trim() || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the call record.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="The call"
      subtitle={`${item.itemKey} — ${item.title}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            Save the call
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="info">
          This is what the next person who speaks to this client will read. The item cannot be
          completed until it is filled in.
        </InlineAlert>

        <Field label="Who called whom" required>
          <Select
            value={direction}
            onChange={(event) =>
              setDirection(event.target.value as ClientContactDirection | "")
            }
          >
            <option value="">Choose…</option>
            {CONTACT_DIRECTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Who you spoke to" required>
            <Input
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              placeholder="Anita Rao"
            />
          </Field>
          <Field label="Which client" required hint="Their organisation, not the project.">
            <Input
              value={clientName}
              onChange={(event) => setClientName(event.target.value)}
              placeholder="Northwind Retail"
            />
          </Field>
        </div>

        <Field label="How you reached them" required>
          <Select
            value={channel}
            onChange={(event) => setChannel(event.target.value as ClientContactChannel | "")}
          >
            <option value="">Choose…</option>
            {CONTACT_CHANNELS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="What you explained"
          required
          hint="Enough that somebody else could pick up the next call without ringing you first."
        >
          <Textarea
            rows={5}
            value={whatWasExplained}
            onChange={(event) => setWhatWasExplained(event.target.value)}
            placeholder="Walked her through exporting the month-end summary, and sent the guide."
          />
        </Field>

        {missing.length > 0 && (
          <InlineAlert tone="warning">
            Saving is fine, but this cannot be completed until it says {inWords(missing)}.
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}

/** "a, b and c" — the same sentence the server's refusal builds. */
function inWords(parts: string[]): string {
  const last = parts[parts.length - 1] ?? "";
  if (parts.length <= 1) {
    return last;
  }
  return `${parts.slice(0, -1).join(", ")} and ${last}`;
}
